import { useMemo } from 'react';
import { useVelvet } from '@/context/VelvetContext';
import { useAuth, canSeeCosts } from '@/context/AuthContext';
import { fmtUSD, fmtPct, isSameDay, daysFromNow } from '@/lib/calc';
import StatusBadge from '@/components/StatusBadge';
import { CalendarDays, TrendingUp, Sparkles } from 'lucide-react';

function Kpi({ label, value, sub, testid }) {
  return (
    <div className="velvet-card p-6" data-testid={testid}>
      <div className="velvet-kpi-label">{label}</div>
      <div className="velvet-kpi-value mt-3">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-3">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { role, profile } = useAuth();
  const { orders, quotations, customers, deliveries, orderStatuses } = useVelvet();
  const showCosts = canSeeCosts(role);

  const stats = useMemo(() => {
    const byStatus = Object.fromEntries(orderStatuses.map((s) => [s, 0]));
    let sales = 0, cost = 0;
    orders.forEach((o) => {
      byStatus[o.status] = (byStatus[o.status] || 0) + 1;
      (o.order_lines || []).forEach((li) => {
        sales += Number(li.unit_price) * Number(li.qty);
        cost += Number(li.unit_cost_snapshot) * Number(li.qty);
      });
    });
    const open = orders.filter((o) => !['Delivered','Cancelled','Failed Delivery'].includes(o.status)).length;
    const pending = quotations.filter((q) => q.status === 'Pending').length;
    return { byStatus, sales, cost, open, pending, margin: sales > 0 ? (sales - cost) / sales : 0 };
  }, [orders, quotations, orderStatuses]);

  const dueToday = orders.filter((o) => o.delivery_date && isSameDay(o.delivery_date, new Date().toISOString()));
  const dueTomorrow = orders.filter((o) => o.delivery_date && daysFromNow(o.delivery_date, 1));

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-8 lg:py-12 max-w-[1400px]" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
        <div>
          <div className="velvet-eyebrow mb-3">Overview · {profile?.full_name}</div>
          <h1 className="velvet-page-title">Good day, Velvet Bloom.</h1>
          <p className="text-sm text-muted-foreground mt-3 max-w-xl">
            A quiet glance at today&rsquo;s atelier — open orders, deliveries in motion, and pending quotations awaiting your touch.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <CalendarDays className="h-4 w-4" />
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'America/Chicago' })}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <Kpi label="Open Orders" value={stats.open} sub={`${stats.byStatus['In Production'] || 0} in production · ${stats.byStatus['Ready'] || 0} ready`} testid="kpi-open-orders" />
        <Kpi label="Pending Quotations" value={stats.pending} sub="Awaiting customer approval" testid="kpi-pending-quotations" />
        <Kpi label="Estimated Sales" value={fmtUSD(stats.sales)} sub={<span className="inline-flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Across all open orders</span>} testid="kpi-est-sales" />
        {showCosts ? (
          <Kpi label="Gross Margin" value={fmtPct(stats.margin)} sub={`${fmtUSD(stats.sales - stats.cost)} gross profit`} testid="kpi-gross-margin" />
        ) : (
          <Kpi label="Deliveries" value={deliveries.length} sub="All tracked deliveries" testid="kpi-deliveries" />
        )}
      </div>

      <section className="velvet-card p-6 lg:p-8 mb-10" data-testid="orders-by-status">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="velvet-section-title">Orders by status</h2>
          <span className="velvet-eyebrow">{orders.length} total</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-11 gap-2">
          {orderStatuses.map((s) => (
            <div key={s} className="border border-border p-3 bg-background/40">
              <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground line-clamp-1">{s}</div>
              <div className="font-serif text-2xl mt-1">{stats.byStatus[s] || 0}</div>
            </div>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 velvet-card p-6 lg:p-8">
          <h2 className="velvet-section-title mb-6">Deliveries — today &amp; tomorrow</h2>
          <DayGroup label="Today" items={dueToday} customers={customers} />
          <div className="mt-6"><DayGroup label="Tomorrow" items={dueTomorrow} customers={customers} /></div>
        </section>
        <section className="velvet-card p-6 lg:p-8">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="velvet-section-title">Pending quotations</h2>
            <Sparkles className="h-4 w-4 text-[hsl(var(--muted-green-dark))]" />
          </div>
          {quotations.filter((q) => q.status === 'Pending').length === 0 ? (
            <p className="text-sm text-muted-foreground">No pending quotations.</p>
          ) : (
            <ul className="divide-y divide-border">
              {quotations.filter((q) => q.status === 'Pending').map((q) => {
                const c = customers.find((x) => x.id === q.customer_id);
                const total = (q.quote_lines || []).reduce((s, li) => s + li.unit_price * li.qty, 0);
                return (
                  <li key={q.id} className="py-4 first:pt-0 last:pb-0">
                    <div className="flex items-baseline justify-between gap-3">
                      <div>
                        <div className="font-serif text-lg">{c?.name || '—'}</div>
                        <div className="text-xs text-muted-foreground">{q.number}{q.is_demo && ' · DEMO'}</div>
                      </div>
                      <div className="velvet-money text-sm">{fmtUSD(total)}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function DayGroup({ label, items, customers }) {
  return (
    <div>
      <div className="velvet-eyebrow mb-3">{label} <span className="text-foreground/40">· {items.length}</span></div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No deliveries scheduled.</p>
      ) : (
        <ul className="divide-y divide-border">
          {items.map((o) => {
            const c = customers.find((x) => x.id === o.customer_id);
            const time = new Date(o.delivery_date).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago' });
            return (
              <li key={o.id} className="py-3 flex items-center gap-4">
                <div className="w-16 shrink-0 font-mono text-sm">{time}</div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{c?.name || '—'}{o.is_demo && <span className="ml-2 text-[10px] px-1.5 py-0.5 border border-border text-muted-foreground">DEMO</span>}</div>
                  <div className="text-xs text-muted-foreground truncate">{o.number} · {o.occasion} · {o.fulfillment}</div>
                </div>
                <StatusBadge status={o.status} />
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
